import { useRef, useCallback, useEffect, useState } from 'react';

const useTimeoutEffect = (effect, deps) => {
    const timeoutIds = useRef([]);
    const timeoutFunc = useCallback((handler, timeout) => {
        const id = setTimeout(handler, timeout);
        timeoutIds.current.push(id);
    }, [timeoutIds]);
    useEffect(() => {
        return effect(timeoutFunc, () => {
            if (timeoutIds.current.length > 0) {
                timeoutIds.current.forEach((id) => {
                    clearTimeout(id);
                });
            }
        });
    }, deps);
    useEffect(() => {
        return function onUnmount() {
            if (timeoutIds.current.length > 0) {
                timeoutIds.current.forEach((id) => {
                    clearTimeout(id);
                });
            }
        };
    }, [timeoutIds]);
};

/**
 * @param callback The callback that is invoked after the timeout expired
 * @param timeout A timeout in milliseconds
 */
function useTimeout(callback, timeout) {
    const timeoutCallback = useRef(callback);
    const [timeoutId, setTimeoutId] = useState(null);
    useEffect(() => {
        timeoutCallback.current = callback;
    }, [callback]);
    useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);
    return useCallback((...args) => {
        const id = setTimeout(() => timeoutCallback.current(...args), timeout);
        setTimeoutId(id);
    }, [timeout]);
}

/**
 * This hook was inspired by Dan Abramov's blogpost:
 * https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 * @param callback
 * @param delay
 */
const useInterval = (callback, delay) => {
    const intervalCallback = useRef(callback);
    useEffect(() => {
        intervalCallback.current = callback;
    }, [callback]);
    useEffect(() => {
        if (delay !== null) {
            const id = setInterval(() => intervalCallback.current(), delay);
            return () => clearInterval(id);
        }
    }, [delay]);
};

const useTimer = (start = 0) => {
    const [val, setVal] = useState(start);
    useInterval(() => setVal(val + 1), 1000);
    return val;
};

const useIdleCallbackEffect = (effect, deps) => {
    if (!window.requestIdleCallback) {
        console.warn('This browser does not support "requestIdleCallback"');
        return;
    }
    const idleCallbackHandle = useRef(null);
    const idleCallbackFunc = useCallback((callback, options) => {
        idleCallbackHandle.current = window.requestIdleCallback(callback, options);
        return idleCallbackHandle.current;
    }, [idleCallbackHandle]);
    useEffect(() => {
        return effect(idleCallbackFunc);
    }, deps);
    useEffect(() => {
        return function onUnmount() {
            if (idleCallbackHandle.current !== null) {
                window.cancelIdleCallback(idleCallbackHandle.current);
            }
        };
    }, [idleCallbackHandle]);
};

/**
 * @param callback The callback that is invoked as soons as the browser invokes the idle callback
 * @param options Options for requestIdleCallback
 */
const useIdleCallback = (callback, options) => {
    if (!window.requestIdleCallback) {
        console.warn('This browser does not support "requestIdleCallback"');
        return callback;
    }
    const ricCallback = useRef(callback);
    const [handle, setHandle] = useState(null);
    useEffect(() => {
        ricCallback.current = callback;
    }, [callback]);
    useEffect(() => {
        return () => {
            if (handle) {
                window.cancelIdleCallback(handle);
            }
        };
    }, [handle]);
    return useCallback((...args) => {
        const h = window.requestIdleCallback(() => ricCallback.current(...args), options);
        setHandle(h);
    }, [options]);
};

/**
 * @param callback The callback that is invoked in the next animation frame
 */
const useAnimationFrame = (callback) => {
    const rafCallback = useRef(callback);
    const [handle, setHandle] = useState(null);
    useEffect(() => {
        rafCallback.current = callback;
    }, [callback]);
    useEffect(() => {
        return () => {
            if (handle) {
                cancelAnimationFrame(handle);
            }
        };
    }, [handle]);
    return useCallback((...args) => {
        const h = requestAnimationFrame(() => rafCallback.current(...args));
        setHandle(h);
    }, []);
};

const useAnimationFrameLoop = (callback, stop = false) => {
    const rafCallback = useRef(callback);
    const stopValue = useRef(false);
    useEffect(() => {
        rafCallback.current = callback;
        stopValue.current = stop;
    }, [callback, stop]);
    const nextCallback = useCallback(() => {
        if (!stopValue.current) {
            rafCallback.current();
        }
        runInLoop();
    }, []);
    const runInLoop = useAnimationFrame(nextCallback);
    useEffect(() => {
        runInLoop();
    }, [runInLoop]);
};

export { useAnimationFrame, useAnimationFrameLoop, useIdleCallback, useIdleCallbackEffect, useInterval, useTimeout, useTimeoutEffect, useTimer };
